import {
	DownloadOutlined,
	LeftOutlined,
	RightOutlined,
} from "@ant-design/icons";
import { Button, Empty, Modal, Spin } from "antd";
import { useEffect, useState } from "react";
import "../../assets/scss/reportDocumentPreview.scss";
import {
	fetchReportDocument,
	getReportDocumentPreviewUrl,
} from "../../services/api-server/welldocs";
import { alertMessage } from "../../utils/alert";
import {
	getReportDocumentImageType,
	getReportDocumentName,
	getReportDocumentPreviewKind,
} from "../../utils/reporting/reportFiles";
import CustomPdfViewer from "../PDF/CustomPdfViewer";

// What a load ended with: the file to show, or a failure.
type LoadedView =
	{ status: "pdf" | "image" | "office"; src: string } | { status: "failed" };

const NO_PREVIEW_MESSAGE = "There's no preview for this type of file.";
const NOT_AVAILABLE_MESSAGE = "Preview isn't available for this file.";

// Why a file is never loaded for a preview, if it isn't: its type has none,
// or it is an Office file SharePoint's viewer can't be asked for, having no
// SharePoint id.
const getUnavailableMessage = (file: any): string | null => {
	const kind = getReportDocumentPreviewKind(file);
	if (kind === "none") return NO_PREVIEW_MESSAGE;
	if (kind === "office" && !file?.sharepoint_id) return NOT_AVAILABLE_MESSAGE;
	return null;
};

const getDownloadName = (file: any): string =>
	file?.actual_name || file?.name || file?.file_name || "download"; // NOSONAR: an empty stored name must fall back too

const saveFromUrl = (href: string, filename: string) => {
	const a = document.createElement("a");
	a.href = href;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
};

// A file fetched to preview: an Office file as a link to SharePoint's viewer,
// a PDF or image as its bytes, typed so the browser shows them as that type.
type FetchedFile =
	{ status: "office"; src: string } | { status: "pdf" | "image"; blob: Blob };

const fetchForPreview = async (
	file: any,
	tenantKey: string,
	folderId: string | undefined,
	signal: AbortSignal
): Promise<FetchedFile> => {
	const kind = getReportDocumentPreviewKind(file);
	if (kind === "office") {
		const src = await getReportDocumentPreviewUrl(
			tenantKey,
			file.sharepoint_id,
			signal
		);
		return { status: "office", src };
	}

	const data = await fetchReportDocument(tenantKey, folderId, file, signal);
	return kind === "pdf"
		? { status: "pdf", blob: new Blob([data], { type: "application/pdf" }) }
		: {
				status: "image",
				blob: new Blob([data], { type: getReportDocumentImageType(file) }),
			};
};

type ReportDocumentPreviewProps = {
	tenantKey: string;
	folderId?: string;
	// The item's documents, stepped through with previous and next.
	documents: any[];
	index: number;
	onIndexChange: (index: number) => void;
	onClose: () => void;
};

// A document attached to a report item, such as a finding, in a modal: a PDF
// in the PDF viewer, an image fitted to the window, a Word, Excel or
// PowerPoint file in SharePoint's viewer, and for anything else a note with
// Download.
function ReportDocumentPreview({
	tenantKey,
	folderId,
	documents,
	index,
	onIndexChange,
	onClose,
}: Readonly<ReportDocumentPreviewProps>) {
	const file = documents[index];
	const name = getReportDocumentName(file);
	const unavailableMessage = getUnavailableMessage(file);
	// Bumped by Retry, to load the same file again.
	const [attempt, setAttempt] = useState(0);
	// The last load and which file and attempt it was for: one for another
	// file shows as loading, so a stale file never shows.
	const [loaded, setLoaded] = useState<{
		file: any;
		attempt: number;
		view: LoadedView;
	} | null>(null);
	// The file being downloaded, so stepping to another file leaves its
	// Download free.
	const [downloadingFile, setDownloadingFile] = useState<any>(null);

	const view =
		loaded !== null && loaded.file === file && loaded.attempt === attempt
			? loaded.view
			: null;

	useEffect(() => {
		if (unavailableMessage) return;

		const controller = new AbortController();
		// Made only once the file arrives for a load still current, so a file
		// no longer on screen never gets one.
		let blobUrl = "";
		fetchForPreview(file, tenantKey, folderId, controller.signal).then(
			(fetched) => {
				if (controller.signal.aborted) return;
				if (fetched.status === "office") {
					setLoaded({ file, attempt, view: fetched });
					return;
				}
				blobUrl = URL.createObjectURL(fetched.blob);
				setLoaded({
					file,
					attempt,
					view: { status: fetched.status, src: blobUrl },
				});
			},
			() => {
				if (controller.signal.aborted) return;
				setLoaded({ file, attempt, view: { status: "failed" } });
			}
		);

		// A file left, closed or retried: its load stops, its blob goes, and so
		// does what it loaded, so coming back to the file loads it afresh rather
		// than showing a revoked blob.
		return () => {
			controller.abort();
			if (blobUrl) URL.revokeObjectURL(blobUrl);
			setLoaded((prev) =>
				prev !== null && prev.file === file && prev.attempt === attempt
					? null
					: prev
			);
		};
	}, [file, attempt, tenantKey, folderId, unavailableMessage]);

	const download = async () => {
		// A PDF or image on screen is already here.
		if (view?.status === "pdf" || view?.status === "image") {
			saveFromUrl(view.src, getDownloadName(file));
			return;
		}

		setDownloadingFile(file);
		try {
			const data = await fetchReportDocument(tenantKey, folderId, file);
			const href = URL.createObjectURL(
				new Blob([data], { type: "application/octet-stream" })
			);
			saveFromUrl(href, getDownloadName(file));
			URL.revokeObjectURL(href);
		} catch {
			alertMessage("error", "Failed to download the file.");
		} finally {
			setDownloadingFile((current: any) => (current === file ? null : current));
		}
	};

	const downloadButton = (
		<Button
			icon={<DownloadOutlined />}
			loading={downloadingFile !== null && downloadingFile === file}
			onClick={() => void download()}
		>
			Download
		</Button>
	);

	const renderBody = () => {
		if (unavailableMessage) {
			return <Empty description={unavailableMessage}>{downloadButton}</Empty>;
		}
		if (!view) return <Spin size="large" />;

		switch (view.status) {
			case "pdf":
				// Keyed by its blob, so each file starts at its own zoom.
				return (
					<CustomPdfViewer
						key={view.src}
						fileUrl={view.src}
						title={name}
						showDownload={false}
					/>
				);
			case "image":
				return (
					<img
						className="report-document-preview__image"
						src={view.src}
						alt={name}
					/>
				);
			case "office":
				return (
					<iframe
						className="report-document-preview__frame"
						src={view.src}
						title={name}
					/>
				);
			default: // "failed"
				return (
					<Empty description="This file couldn't be opened.">
						<div className="report-document-preview__retry">
							<Button onClick={() => setAttempt((value) => value + 1)}>
								Retry
							</Button>
							{downloadButton}
						</div>
					</Empty>
				);
		}
	};

	const count = documents.length;

	return (
		<Modal
			open
			centered
			width={1100}
			className="report-document-preview"
			classNames={{ body: "report-document-preview__body" }}
			title={
				<span className="report-document-preview__title" title={name}>
					{name}
				</span>
			}
			onCancel={onClose}
			footer={
				<div className="report-document-preview__footer">
					{count > 1 && (
						<div className="report-document-preview__pager">
							<Button
								icon={<LeftOutlined />}
								aria-label="Previous file"
								disabled={index === 0}
								onClick={() => onIndexChange(index - 1)}
							/>
							<span
								className="report-document-preview__position"
								aria-live="polite"
							>
								{`${index + 1} of ${count}`}
							</span>
							<Button
								icon={<RightOutlined />}
								aria-label="Next file"
								disabled={index === count - 1}
								onClick={() => onIndexChange(index + 1)}
							/>
						</div>
					)}
					<div className="report-document-preview__actions">
						{downloadButton}
						<Button onClick={onClose}>Close</Button>
					</div>
				</div>
			}
		>
			{renderBody()}
		</Modal>
	);
}

export default ReportDocumentPreview;
